import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2,
    ArrowRight,
    FileText,
    Upload,
    Download,
    Receipt,
    FileBadge,
    Truck
} from 'lucide-react';
import './Solutions.css';
import '../LandingPage.css';

import tmsLogo from '../../../assets/TmsLogoh.png';

const DOC_TYPES = [
    {
        icon: <FileText size={28} color="#4338ca" />,
        bg: '#eef2ff',
        title: 'Documents de Mission (CMR)',
        description: 'Attachez directement à chaque mission les documents de transport associés : lettres de voiture (CMR), devis, bons de commande. Accessibles en un clic depuis la fiche mission.'
    },
    {
        icon: <Truck size={28} color="#059669" />,
        bg: '#ecfdf5',
        title: 'Contrat de Sous-traitance',
        description: 'Le contrat de sous-traitance est généré automatiquement pour chaque mission confiée à un prestataire. Téléchargez-le en PDF directement depuis la fiche de mission sous-traitée.'
    },
    {
        icon: <Receipt size={28} color="#b45309" />,
        bg: '#fef9c3',
        title: 'Factures Clients',
        description: 'Créez et prévisualisez vos factures directement dans le TMS. Téléchargez-les en PDF, suivez leur statut (brouillon, envoyée, payée) et émettez des avoirs en cas de litige.'
    },
    {
        icon: <FileBadge size={28} color="#7c3aed" />,
        bg: '#f5f3ff',
        title: 'Facturation Electronique (PDP)',
        description: 'Soumettez vos factures à votre plateforme de dématérialisation partenaire (PDP) en un clic et rafraîchissez leur statut pour rester conforme aux obligations légales de e-facturation.'
    }
];

const DocumentsCentralises = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="saas-container">
            {/* Navbar */}
            <nav className="landing-nav saas-nav">
                <div className="nav-container">
                    <div className="nav-logo">
                        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
                            <img src={tmsLogo} alt="Bfret Logo" style={{ height: '32px' }} />
                        </Link>
                    </div>
                    <div className="nav-links">
                        <div className="nav-dropdown">
                            <span className="nav-dropdown-toggle">Solutions</span>
                            <div className="nav-dropdown-menu">
                                <Link to="/solutions/planification-intelligente">Planification intelligente</Link>
                                <Link to="/solutions/suivi-temps-reel">Suivi en temps réel</Link>
                                <Link to="/solutions/documents-centralises">Documents centralisés</Link>
                                <Link to="/solutions/rapports-analytics">Rapports & Analytics</Link>
                                <Link to="/solutions/gestion-ressources">Gestion des ressources</Link>
                                <Link to="/solutions/ai-agent-center">AI Agent Center</Link>
                            </div>
                        </div>
                        <Link to="/contact" className="btn-primary saas-btn-primary" style={{ marginLeft: '1rem' }}>Démo</Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section style={{ padding: '6rem 5% 4rem', textAlign: 'center', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <span className="saas-feature-tag" style={{ display: 'inline-block', marginBottom: '1.5rem', backgroundColor: '#e0e7ff', color: '#4338ca', padding: '0.5rem 1rem', borderRadius: '9999px', fontWeight: '600' }}>Documents & Facturation</span>
                <h1 style={{ fontSize: '3.5rem', maxWidth: '950px', margin: '0 auto 1.5rem', color: '#0f172a', lineHeight: '1.1' }}>
                    Tous vos documents au bon endroit
                </h1>
                <p style={{ fontSize: '1.25rem', maxWidth: '750px', margin: '0 auto 2.5rem', color: '#475569', lineHeight: '1.6' }}>
                    CMR, contrats de sous-traitance, factures clients... Centralisez, gérez et téléchargez tous vos documents directement depuis votre TMS, sans jongler entre plusieurs outils.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <Link to="/contact" className="saas-btn saas-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.1rem', borderRadius: '8px' }}>
                        Demander une démo
                    </Link>
                </div>
            </section>

            {/* Quick Benefits Grid */}
            <section style={{ padding: '4rem 5%', backgroundColor: '#ffffff' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '1rem' }}>Fini les documents perdus</h3>
                        <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>Chaque document est attaché à sa mission ou à sa facture. Retrouvez n'importe quel fichier en quelques secondes.</p>
                    </div>
                    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '1rem' }}>PDF téléchargeables à tout moment</h3>
                        <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>Contrats, factures et CMR sont disponibles en téléchargement immédiat pour vos archivages ou vos échanges clients.</p>
                    </div>
                    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '1rem' }}>Du terrain au bureau sans attente</h3>
                        <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>Le chauffeur prend une photo du CMR depuis l'application mobile — elle remonte instantanément dans la fiche mission.</p>
                    </div>
                </div>
            </section>

            {/* Section 1: Mission Documents (upload/download) */}
            <section className="saas-feature-section" style={{ paddingTop: '5rem', paddingBottom: '4rem', borderTop: '1px solid #f1f5f9' }}>
                <div className="saas-feature-grid">
                    <div className="saas-feature-text" style={{ paddingRight: '2rem' }}>
                        <span className="saas-feature-tag">Documents de Mission</span>
                        <h2 style={{ fontSize: '2.5rem', lineHeight: '1.2', marginBottom: '1.5rem', color: '#0f172a' }}>
                            CMR et pièces jointes par mission
                        </h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2.5rem', lineHeight: '1.6' }}>
                            Chaque mission peut embarquer ses propres documents. Que ce soit une lettre de voiture, un bon de commande ou un devis, tout est attaché directement à la fiche concernée.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Téléversement par le transporteur
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    L'exploitant peut joindre des documents depuis le bureau directement sur la fiche de mission sous-traitée.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Remontée terrain via l'application
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Le chauffeur prend en photo le CMR depuis son téléphone et le document apparaît immédiatement côté bureau.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Accès expéditeur inclus
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    L'expéditeur peut lui aussi consulter et déposer des documents sur ses propres missions, sans intermédiaire.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="saas-feature-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {/* Visual mockup - document cards stack */}
                        <div style={{ position: 'relative', width: '100%', maxWidth: '520px' }}>
                            <div style={{ position: 'absolute', inset: '-10px', background: 'linear-gradient(to right, #e0e7ff, #f3e8ff)', filter: 'blur(30px)', opacity: '0.5', borderRadius: '20px', zIndex: 0 }}></div>
                            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {[
                                    { icon: <FileText size={22} color="#4338ca" />, label: 'CMR_Livraison_Paris.pdf', tag: 'CMR', tagColor: '#4338ca', tagBg: '#eef2ff' },
                                    { icon: <FileText size={22} color="#059669" />, label: 'Bon_Commande_Client_ABC.pdf', tag: 'Bon de commande', tagColor: '#059669', tagBg: '#ecfdf5' },
                                    { icon: <FileText size={22} color="#b45309" />, label: 'Devis_Transport_2026.pdf', tag: 'Devis', tagColor: '#b45309', tagBg: '#fef9c3' },
                                ].map((doc, i) => (
                                    <div key={i} style={{ padding: '1.25rem 1.5rem', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                        <div style={{ padding: '0.6rem', backgroundColor: '#f1f5f9', borderRadius: '8px', flexShrink: 0 }}>
                                            {doc.icon}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.label}</p>
                                            <span style={{ fontSize: '0.8rem', backgroundColor: doc.tagBg, color: doc.tagColor, padding: '0.15rem 0.5rem', borderRadius: '9999px', fontWeight: '600' }}>{doc.tag}</span>
                                        </div>
                                        <Download size={18} color="#94a3b8" style={{ flexShrink: 0 }} />
                                    </div>
                                ))}
                                <div style={{ padding: '1.25rem 1.5rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <Upload size={22} color="#94a3b8" />
                                    <span style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Ajouter un document (CMR, devis...)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 2: Invoices */}
            <section className="saas-feature-section alt-bg" style={{ paddingTop: '5rem', paddingBottom: '5rem', backgroundColor: '#f8fafc' }}>
                <div className="saas-feature-grid reverse">
                    <div className="saas-feature-text" style={{ paddingLeft: '2rem' }}>
                        <span className="saas-feature-tag">Facturation</span>
                        <h2 style={{ fontSize: '2.5rem', lineHeight: '1.2', marginBottom: '1.5rem', color: '#0f172a' }}>
                            Créez et gérez vos factures clients
                        </h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2.5rem', lineHeight: '1.6' }}>
                            Générez vos factures directement depuis le TMS, téléchargez-les en PDF, suivez leur statut de paiement et soumettez-les à votre plateforme de facturation électronique.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Création & aperçu PDF
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Composez votre facture, prévisualisez-la avant envoi, puis téléchargez le PDF final en un clic.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Suivi du statut de paiement
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Chaque facture passe par des statuts clairs (brouillon, envoyée, payée) pour ne laisser aucun impayé dans l'oubli.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    E-Facturation & avoir
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Soumettez vos factures à votre PDP (plateforme de dématérialisation) et émettez des avoirs directement depuis l'interface.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Contrat de sous-traitance PDF
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Pour chaque mission sous-traitée, le contrat est généré automatiquement et téléchargeable en PDF depuis la fiche de mission.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="saas-feature-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {/* Invoice status mockup */}
                        <div style={{ position: 'relative', width: '100%', maxWidth: '480px' }}>
                            <div style={{ position: 'absolute', inset: '-10px', background: 'linear-gradient(to left, #e0e7ff, #f3e8ff)', filter: 'blur(30px)', opacity: '0.5', borderRadius: '20px', zIndex: 0 }}></div>
                            <div style={{ position: 'relative', zIndex: 1, backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.12)' }}>
                                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '1rem' }}>Mes Factures</span>
                                    <span style={{ fontSize: '0.85rem', color: '#4338ca', fontWeight: '600' }}>+ Nouvelle facture</span>
                                </div>
                                {[
                                    { num: 'FA-2026-081', client: 'Entreprise Dupont', amount: '2 450,00 €', status: 'Payée', statusColor: '#059669', statusBg: '#ecfdf5' },
                                    { num: 'FA-2026-080', client: 'Transport Martin', amount: '1 200,00 €', status: 'Envoyée', statusColor: '#b45309', statusBg: '#fef9c3' },
                                    { num: 'FA-2026-079', client: 'Logistics ABC', amount: '3 800,00 €', status: 'Brouillon', statusColor: '#475569', statusBg: '#f1f5f9' },
                                ].map((inv, i) => (
                                    <div key={i} style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #f8fafc', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ margin: '0 0 0.2rem', fontSize: '0.9rem', fontWeight: '600', color: '#0f172a' }}>{inv.num}</p>
                                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>{inv.client}</p>
                                        </div>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a' }}>{inv.amount}</span>
                                        <span style={{ fontSize: '0.78rem', fontWeight: '600', padding: '0.2rem 0.6rem', borderRadius: '9999px', backgroundColor: inv.statusBg, color: inv.statusColor }}>{inv.status}</span>
                                        <Download size={16} color="#94a3b8" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4 document types icon grid */}
            <section style={{ padding: '5rem 5%', backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                        <h2 style={{ fontSize: '2.5rem', color: '#0f172a', marginBottom: '1rem' }}>4 types de documents gérés</h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', maxWidth: '600px', margin: '0 auto' }}>
                            Un seul endroit pour tous vos documents, du terrain au comptable.
                        </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
                        {DOC_TYPES.map((doc, i) => (
                            <div key={i} style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div style={{ padding: '0.75rem', backgroundColor: doc.bg, borderRadius: '10px', width: 'fit-content' }}>
                                    {doc.icon}
                                </div>
                                <h3 style={{ fontSize: '1.1rem', color: '#0f172a', margin: 0, fontWeight: '600' }}>{doc.title}</h3>
                                <p style={{ color: '#475569', margin: 0, lineHeight: '1.6', fontSize: '0.97rem' }}>{doc.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Metrics Banner */}
            <section style={{ backgroundColor: '#0f172a', color: 'white', padding: '5rem 5%' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '3rem', textAlign: 'center' }}>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>4</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Types de documents</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>PDF</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Téléchargement instantané</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>0</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Document perdu</div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="saas-hero" style={{ padding: '6rem 5%', textAlign: 'center', backgroundColor: '#ffffff' }}>
                <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem', color: '#0f172a' }}>Gérez vos documents sans effort</h2>
                <p style={{ maxWidth: '600px', margin: '0 auto 2.5rem', fontSize: '1.15rem', color: '#475569', lineHeight: '1.6' }}>
                    Arrêtez de chercher vos fichiers dans des emails ou des dossiers partagés. Tout est ici, attaché au bon endroit.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <Link to="/contact" className="saas-btn saas-btn-primary" style={{ padding: '1rem 2.5rem', fontSize: '1.15rem', borderRadius: '8px' }}>
                        Contacter notre équipe <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default DocumentsCentralises;
